import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/800.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/jetbrains-mono';
import './App.css';
import './i18n';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import DocumentLibrary from './pages/DocumentLibrary';
import DocumentEditor from './pages/DocumentEditor';
import DocumentViewer from './pages/DocumentViewer';
import ChatPage from './pages/ChatPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CategoriesPage from './pages/CategoriesPage';
import UserManagement from './pages/UserManagement';
import WorkspaceManagement from './pages/WorkspaceManagement';
import WorkspaceSettings from './pages/WorkspaceSettings';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import PublicPortal from './pages/PublicPortal';
import PublicDocument from './pages/PublicDocument';
import ContactRequests from './pages/ContactRequests';
import AIStudio from './pages/AIStudio';
import ChatbotSettings from './pages/ChatbotSettings';
import ChatbotBuilder from './pages/ChatbotBuilder';
import News from './pages/News';
import Roadmap from './pages/Roadmap';
import Forms from './pages/Forms';
import PublicNews from './pages/PublicNews';
import PublicRoadmap from './pages/PublicRoadmap';
import PublicForms from './pages/PublicForms';
import WidgetEmbed from './pages/WidgetEmbed';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Component to check for OAuth callback in URL hash
function AppRouter({ user, setUser, loading, customDomainWorkspace }) {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-lg text-zinc-600">Loading...</div>
      </div>
    );
  }

  // If accessed via custom domain, show public portal directly
  if (customDomainWorkspace) {
    const hostname = window.location.hostname;
    return (
      <Routes>
        <Route path="/" element={<PublicPortal customDomain={hostname} />} />
        <Route path="/document/:documentId" element={<PublicDocument customDomain={hostname} />} />
        <Route path="*" element={<PublicPortal customDomain={hostname} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={!user ? <AuthPage setUser={setUser} /> : <Navigate to="/" />} />
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/documents" element={user ? <DocumentLibrary user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/documents/new" element={user ? <DocumentEditor user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/documents/:id" element={user ? <DocumentViewer user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/documents/:id/edit" element={user ? <DocumentEditor user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/chat" element={user ? <ChatPage user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/categories" element={user ? <CategoriesPage user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/analytics" element={user ? <AnalyticsPage user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/users" element={user ? <UserManagement user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/workspaces" element={user ? <WorkspaceManagement user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/admin" element={user ? <SuperAdminDashboard user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/settings" element={user ? <WorkspaceSettings user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/contact-requests" element={user ? <ContactRequests user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/ai-studio" element={user ? <AIStudio user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/chatbot-settings" element={user ? <ChatbotSettings user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/chatbot-builder" element={user ? <ChatbotBuilder user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/news" element={user ? <News user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/roadmap" element={user ? <Roadmap user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      <Route path="/forms" element={user ? <Forms user={user} setUser={setUser} /> : <Navigate to="/auth" />} />
      {/* Public Routes */}
      <Route path="/portal/:subdomain" element={<PublicPortal />} />
      <Route path="/portal/:subdomain/helpdesk" element={<PublicPortal />} />
      <Route path="/portal/:subdomain/news" element={<PublicNews />} />
      <Route path="/portal/:subdomain/roadmap" element={<PublicRoadmap />} />
      <Route path="/portal/:subdomain/forms" element={<PublicForms />} />
      <Route path="/portal/:subdomain/forms/:slug" element={<PublicForms />} />
      <Route path="/widget-embed/:subdomain" element={<WidgetEmbed />} />
      <Route path="/portal/:subdomain/document/:documentId" element={<PublicDocument />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customDomainWorkspace, setCustomDomainWorkspace] = useState(null);

  useEffect(() => {
    // Check if accessing via custom domain
    const hostname = window.location.hostname;
    const knownDomains = ['localhost', 'preview.emergentagent.com', 'emergent.sh'];
    const isCustomDomain = !knownDomains.some(d => hostname.includes(d));
    
    if (isCustomDomain) {
      // Try to resolve workspace by custom domain
      axios.get(`${API_URL}/public/workspaces/${hostname}`)
        .then(res => {
          setCustomDomainWorkspace(res.data);
          setLoading(false);
        })
        .catch(() => {
          // Not a valid custom domain, proceed normally
          checkAuth();
        });
    } else {
      checkAuth();
    }
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API_URL}/auth/me`)
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-right" />
        <AppRouter 
          user={user} 
          setUser={setUser} 
          loading={loading} 
          customDomainWorkspace={customDomainWorkspace} 
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
export { API_URL };